<script setup lang="ts">
import AppIcon from '@/components/ui/AppIcon.vue'
import { APP_NAME } from '@/lib/constants'
import logoUrl from '@/assets/reybex-logo.png'

withDefaults(
  defineProps<{
    /** Cap sits above the wordmark, as on the sign-in screen. */
    stacked?: boolean
    /** Keep the product name visible on small screens. */
    showName?: boolean
    /** Sign-in lockup, about a third larger than the header mark. */
    large?: boolean
    /** Login and register: the Academy lockup, not the configured app name. */
    academy?: boolean
    /** Top bar: keep only the mark on the narrowest screens. */
    collapse?: boolean
    /** Header splits the wordmark (left) from the cap and product name (center). */
    part?: 'all' | 'logo' | 'title'
  }>(),
  { stacked: false, showName: false, large: false, academy: false, collapse: false, part: 'all' },
)

/** The logo image already spells "Reybex", so only the rest of the name is typed out next to it. */
const nameSuffix = APP_NAME.replace(/^Reybex\s*/i, '') || APP_NAME
</script>

<template>
  <RouterLink
    to="/"
    class="relative inline-flex items-center font-semibold text-gray-900 dark:text-white"
    :class="stacked ? 'flex-col gap-2' : large ? 'gap-x-4' : 'gap-x-3'"
  >
    <AppIcon
      v-if="part !== 'logo' && !(academy && !stacked)"
      name="graduation-cap"
      class="shrink-0 text-[#0E2424] dark:text-white"
      :class="[
        large ? '!size-10' : '!size-7',
        stacked && academy && (large ? '-translate-x-px' : '-translate-x-2'),
        part === 'title' && 'absolute end-full top-1/2 me-3 -translate-y-1/2',
      ]"
    />
    <span v-if="part === 'title'" class="text-[1.35rem] leading-none">{{ APP_NAME }}</span>
    <span
      v-else
      class="inline-flex items-center"
      :class="
        academy
          ? large
            ? 'gap-x-3'
            : stacked
              ? 'gap-x-2.5'
              : 'gap-x-2'
          : large
            ? 'gap-x-2.5'
            : 'gap-x-2'
      "
    >
      <img
        :src="logoUrl"
        alt="Reybex"
        width="300"
        height="68"
        class="w-auto dark:brightness-0 dark:invert"
        :class="large ? 'h-[34px]' : academy && !stacked ? 'h-[19.2px]' : 'h-6'"
      />
      <template v-if="part === 'all' && academy">
        <span
          class="w-px shrink-0 bg-neutral-300 dark:bg-neutral-600"
          :class="[large ? 'h-8' : stacked ? 'h-6' : 'h-[19.2px]', collapse && 'hidden sm:block']"
          aria-hidden="true"
        />
        <span
          class="me-[-0.22em] font-medium uppercase leading-none tracking-[0.22em] text-neutral-500 dark:text-neutral-400"
          :class="[
            large ? 'translate-y-px text-[1.6rem]' : stacked ? 'text-xl' : 'text-base',
            collapse && 'hidden sm:inline',
          ]"
          >Academy</span
        >
      </template>
      <span
        v-else-if="part === 'all'"
        class="leading-none"
        :class="
          large ? 'text-[1.8rem]' : showName ? 'text-[1.35rem]' : 'hidden text-[1.35rem] sm:block'
        "
      >
        | {{ nameSuffix }}</span
      >
    </span>
  </RouterLink>
</template>
